/* @flow */

import { fetchGraphql } from '../utils/ajax';
import type { GraphqlRequest, CallApi } from '../utils/ajax';
import { getJSON } from 'redux-api-middleware';
import { get } from 'lodash';

export type State = {
  isFetching: bool,
  items: any,
  count: number
}
export type Action = { type: string, payload: {data: any} }
export type ProjectList = { ids: [string], first?: number, offset?: number, order?: string, sort?: string }

export let PROJECTS_REQUEST = 'gdc/PROJECTS_REQUEST';
export let PROJECTS_SUCCESS = 'gdc/PROJECTS_SUCCESS';
export let PROJECTS_FAILURE = 'gdc/PROJECTS_FAILURE';
export let PROJECTS_CREATED_REQUEST = 'gdc/PROJECTS_CREATED_REQUEST';
export let PROJECTS_CREATED_SUCCESS = 'gdc/PROJECTS_CREATED_SUCCESS';
export let PROJECTS_CREATED_FAILURE = 'gdc/PROJECTS_CREATED_FAILURE';
export let PROJECT_IS_SUBMITTING = 'gdc/PROJECT_IS_SUBMITTING';

function queryProjectsCreatedTime({ ids, first = 0, offset = 0 }: ProjectList): GraphqlRequest {
  let filter = `(
    not: {state: $projectState},
    project_id: $projectIds,
    first: $first,
    offset: $offset
  )`;
  return {
    variables: {
      projectIds: ids,
      projectState: `legacy`,
      first: first,
      offset: offset
    },
    query: `query ProjectsQueryCreatedTime (
      $first: Int
      $projectState: String
      $projectIds: [String]
      $offset: Int
    ) {
      project ${filter} {
        project_id
      }
      ${ids.map(id => `${id.replace(/-/g, '_')}: transaction_log(project_id: "${id}" first: 1 order_by_desc: "created_datetime") { created_datetime }`)}
    }`
  };
}

function queryProjects({ ids, first = 0, offset = 0 }: ProjectList): GraphqlRequest {
  let filter = `(
    not: {state: $projectState},
    project_id: $projectIds,
    first: $first,
    offset: $offset
  )`;

  return {
    variables: {
      projectIds: ids,
      projectState: `legacy`,
      first,
      offset
    },
    query: `query ProjectsQuery (
      $first: Int
      $projectState: String
      $projectIds: [String]
      $offset: Int
    ) {
      count: _project_count (project_id: $projectIds)
      project ${filter} {
        id,
        project_id,
        state,
        released,
        releasable,
        release_requested,
        in_review,
        submission_enabled,
        request_submission,
        code,
        name,
        primary_site,
        programs {
          name
        },
        dbgap_accession_number
      }
    }`
  };
}

export function fetchProjectsCreatedTime(params: ProjectList): CallApi {
  return fetchGraphql(
    [PROJECTS_CREATED_REQUEST, PROJECTS_CREATED_SUCCESS, PROJECTS_CREATED_FAILURE],
    queryProjectsCreatedTime(params)
  );
}

export function fetchProjects(params: ProjectList): CallApi {
  return (dispatch, getState) => {
    const userProjects = {
      ...get(getState(), 'auth.user.projects.gdc_ids'),
      ...get(getState(), 'auth.user.projects.phs_ids')
    };
    const readableProjects = Object.keys(userProjects).filter(k => userProjects[k].includes('read') || userProjects[k].includes('read_report'));
    dispatch(fetchGraphql(
      [PROJECTS_REQUEST, {
        type: PROJECTS_SUCCESS,
        payload: (action, state, res) => {
          const json = getJSON(res);
          json.then(({ data }) =>  {
            dispatch(fetchProjectsCreatedTime({ ids: data.project.map(p => p.project_id), first: params.first, offset: params.offset }));
          });
          return json;
        }
      }, PROJECTS_FAILURE],
      queryProjects({
        ...params,
        ids: params.ids.filter(id => readableProjects.includes(id))
      })
    ));
  };
}

export function projectIsSubmitting(projectId: string, isSubmitting: boolean): Object {
  return {
    type: PROJECT_IS_SUBMITTING,
    payload: { projectId, isSubmitting},
  };
}

export default function reducer(state: State = {
  isFetching: false,
  items: [],
  count: 0,
}, action: Action): State {
  switch (action.type) {
  case PROJECTS_REQUEST:
    return {
      ...state,
      isFetching: true
    };
  case PROJECTS_SUCCESS:
    const { project, count } = action.payload.data;

    return {
      ...state,
      isFetching: false,
      items: project.map(p => {
        const prevP = state.items.find(({code}) => p.code === code) || { isSubmitting: false };
        return {
          ...p,
          isSubmitting: prevP.isSubmitting
        };
      }),
      count: count,
    };
  case PROJECTS_FAILURE:
    return {
      ...state,
      isFetching: false
    };
  case PROJECTS_CREATED_REQUEST:
    return {
      ...state,
      isFetchingCreated: true,
    };
  case PROJECTS_CREATED_SUCCESS:
    return {
      ...state,
      isFetchingCreated: false,
      items: state.items.map(item => ({
        ...item,
        last_transaction_datetime: (action.payload.data?.[item.project_id.replace(/-/g, '_')]?.[0] || { created_datetime: null }).created_datetime
      })).sort((a, b) => new Date(b.last_transaction_datetime) - new Date(a.last_transaction_datetime)),
    };
  case PROJECTS_CREATED_FAILURE:
    return {
      ...state,
      isFetchingCreated: false,
    };
  case PROJECT_IS_SUBMITTING:
    return {
      ...state,
      items: [
        ...state.items.filter(x => x.project_id !== action.payload.projectId),
        {
          ...state.items.find(x => x.project_id === action.payload.projectId),
          isSubmitting: action.payload.isSubmitting
        }
      ]
    };
  default:
    return state;
  }
}
